import { activeFeatureIntrosAtom } from '@smart-tv/authenticated-app';
import { useAtomValue } from 'jotai';
import React, { useEffect, useState, useCallback } from 'react';
import { FeatureIntroLocation } from '@smart-tv/typings/content';

import { isErrorDialogOpenAtom } from '@smart-tv/atoms';
import Dialog from '@smart-tv/components/dialog';
import useFeatureIntros from '@smart-tv/hooks/use-feature-intros';
import type { FeatureIntro as FeatureIntroData } from '@smart-tv/typings/content';

import FeatureIntroSlider from './feature-intro-slider';
import styles from './feature-intro.module.scss';

export type props = {
  typeOfPlayer: FeatureIntroLocation;
};

export const FeatureIntroPlayer = ({ typeOfPlayer }: props) => {
  const featureIntros = useAtomValue(activeFeatureIntrosAtom);
  const errorOpen = useAtomValue(isErrorDialogOpenAtom);
  const { getSeenFeatureIntroIds, markFeatureIntroAsSeen } = useFeatureIntros();

  const [featureIntro, setFeatureIntro] = useState<FeatureIntroData | null>(null);

  const canShowFrontPageIntro = true;

  useEffect(() => {
    if (!featureIntros || featureIntros.length === 0) return;

    const seenFeatureIntroIds = getSeenFeatureIntroIds();
    const playerIntros = featureIntros.filter(
      (intro) =>
        (intro.locationToShowIntro === typeOfPlayer ||
          intro.locationToShowIntro === FeatureIntroLocation.PLAYER) &&
        intro.steps.length > 0 &&
        !seenFeatureIntroIds.includes(intro.id)
    );
    if (playerIntros.length === 0) return;
    const intros = {
      ...playerIntros[0],
      steps: playerIntros.flatMap((intro) => intro.steps.map((step) => ({ ...step, id: intro.id })))
    };

    markFeatureIntroAsSeen(intros.id);
    setFeatureIntro(intros);
  }, [
    typeOfPlayer,
    canShowFrontPageIntro,
    featureIntros,
    getSeenFeatureIntroIds,
    markFeatureIntroAsSeen
  ]);

  const steps = featureIntro ? featureIntro.steps : [];
  const open = !!featureIntro && !errorOpen;

  const handleClose = useCallback(() => {
    setFeatureIntro(null);
  }, []);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      showCloseButton={false}
      className={styles['feature-intro']}
      dialogContainerProps={{
        className: `${styles['feature-intro__container']} ${styles['feature-intro__container--player']}`,
        'aria-label': featureIntro ? featureIntro.name : undefined
      }}
    >
      <FeatureIntroSlider
        key={featureIntro ? featureIntro.id : undefined}
        steps={steps}
        onClose={handleClose}
        player={true}
      />
    </Dialog>
  );
};

export default FeatureIntroPlayer;
